import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ShoppingBag, ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { fetchEbayStatusRequest } from "../../redux/ebay/ebay.actions";
import { toastify } from "../../configs/utils";

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 2px 6px;
`;

const ConnectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
  padding: ${(props) => (props.$collapsed ? "0.75rem" : "0.75rem 1rem")};
  background: none;
  border: none;
  color: ${props => props.$connected ? "#10b981" : "#9ca3af"};
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 0.75rem;
  font-size: 0.8rem;
  font-weight: 500;
  width: 100%;
  border-radius: 12px;
  position: relative;
  white-space: nowrap;

  &:hover {
    background: rgba(59, 130, 246, 0.18);
    color: ${props => props.$connected ? "#10b981" : "#f9fafb"};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$connected ? "#10b981" : "#6b7280"};
  flex-shrink: 0;
  margin-left: auto;
  box-shadow: ${props => props.$connected ? "0 0 4px rgba(16, 185, 129, 0.5)" : "none"};
  transition: all 0.2s ease;
`;

const ChevronIcon = styled(ChevronDown)`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => props.$open ? "rotate(180deg)" : "rotate(0deg)"};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #1f2937;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  z-index: 10;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? "visible" : "hidden"};
  transform: ${props => props.$open ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.2s ease;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: ${props => props.$danger ? "#ef4444" : "#f9fafb"};
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function ConnectToEbayButton({ collapsed = false }) {
  const dispatch = useDispatch();
  const isEbayConnected = useSelector((state) => state.ebay.connected);
  const isCheckingStatus = useSelector((state) => state.ebay.loading);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const oauthPendingRef = useRef(false);

  const ensureHttps = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") && !url.includes("localhost")) {
      return url.replace("http://", "https://");
    }
    return url;
  };

  const DEFAULT_BASE_URL =
    "https://stickerchecker-new-722491567910.europe-west3.run.app";

  const API_BASE_URL = ensureHttps(
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
  );

  // Re-check status when the window regains focus after the user completes
  // the eBay OAuth login, which happens in a separate tab.
  useEffect(() => {
    const handleFocus = () => {
      if (oauthPendingRef.current) {
        oauthPendingRef.current = false;
        dispatch(fetchEbayStatusRequest());
      }
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const handleConnectToEbay = async () => {
    setIsDropdownOpen(false);
    
    // If already connected, don't redirect
    if (isEbayConnected) {
      return;
    }
    
    try {
      const accessToken = localStorage.getItem("access");
      const refreshToken = localStorage.getItem("refresh");
      
      // Verify tokens exist
      if (!accessToken && !refreshToken) {
        alert("No authentication tokens found. Please login again.");
        return;
      }
      
      let token = accessToken;
      if (!token) {
        token = await refreshAccessToken();
      }

      const ebaySignUrl = `${API_BASE_URL}/api/v1/ebay/ebaysign/`;

      const requestConfig = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      };

      let response = await fetch(ebaySignUrl, requestConfig);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Request failed: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.url) {
        // Open eBay sign-in URL in a new tab; status is re-checked when
        // this tab regains focus after the OAuth flow completes.
        oauthPendingRef.current = true;
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        // If no URL but connected is true, refresh status from the server
        if (data.connected === true) {
          dispatch(fetchEbayStatusRequest());
        }
        alert("Failed to get eBay sign-in URL. Please try again.");
      }
    } catch (error) {
      console.error("eBay connection error:", error);
      alert(error.message || "Failed to connect to eBay. Please try again.");
    }
  };

  const handleDisconnectFromEbay = async () => {
    setIsDropdownOpen(false);
    
    if (!isEbayConnected) {
      return;
    }

    try {
      const accessToken = localStorage.getItem("access");
      const refreshToken = localStorage.getItem("refresh");
      
      // Verify tokens exist
      if (!accessToken && !refreshToken) {
        alert("No authentication tokens found. Please login again.");
        return;
      }
      
      let token = accessToken;
      if (!token) {
        token = await refreshAccessToken();
      }

      const ebayDisconnectUrl = `${API_BASE_URL}/api/v1/ebay/disconnect/`;

      const requestConfig = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      };

      let response = await fetch(ebayDisconnectUrl, requestConfig);

      // Handle 401 by refreshing token and retrying
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        const retryConfig = {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        };
        response = await fetch(ebayDisconnectUrl, retryConfig);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            errorData.message ||
            `Request failed: ${response.status}`
        );
      }

      // Refresh status after successful disconnect
      dispatch(fetchEbayStatusRequest());
      alert("Successfully disconnected from eBay.");
    } catch (error) {
      console.error("eBay disconnection error:", error);
      alert(error.message || "Failed to disconnect from eBay. Please try again.");
    }
  };

  const toggleDropdown = () => {
    if (!isCheckingStatus) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <ConnectButton
        onClick={toggleDropdown}
        $connected={isEbayConnected}
        disabled={isCheckingStatus}
        $collapsed={collapsed}
        title={
          isCheckingStatus
            ? "Checking..."
            : isEbayConnected
            ? "Connected to eBay"
            : "Connect to eBay"
        }
      >
        <ShoppingBag size={20} />
        {!collapsed && (
          <>
            <span>
              {isCheckingStatus
                ? "Checking..."
                : isEbayConnected
                ? "Connected to eBay"
                : "Connect to eBay"}
            </span>
            <StatusIndicator $connected={isEbayConnected} />
            <ChevronIcon size={16} $open={isDropdownOpen} />
          </>
        )}
      </ConnectButton>
      <DropdownMenu $open={isDropdownOpen && !collapsed}>
        {isEbayConnected ? (
          <DropdownItem 
            onClick={handleDisconnectFromEbay}
            $danger={true}
            disabled={isCheckingStatus}
          >
            Disconnect from eBay
          </DropdownItem>
        ) : (
          <DropdownItem 
            onClick={handleConnectToEbay}
            disabled={isCheckingStatus}
          >
            Connect to eBay
          </DropdownItem>
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
}

export default ConnectToEbayButton;

